import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

/**
 * `boom_screen && !boom_crm` — a partner in the BoomScreen ecosystem with no CRM
 * at all. After the CRM-759 migration a free-tier partner is on the Core plan and
 * therefore HAS boom_crm, so this no longer describes the free tier; it only
 * matches partners who were never migrated. Used by the notification filter,
 * which needs a per-notification-type entitlement mapping before it can migrate.
 */
export const useIsBoomScreenOnly = () => {
  const { type } = useAuthContext();
  return type.isBoomScreen && !type.isBoomCRM;
};

/**
 * A CRM feature's content is gated (show a "Book a demo" upsell instead) when the
 * partner is in the CRM ecosystem but the entitlement is absent — i.e. a Core
 * partner hitting a Pro feature, or any partner whose entitlement an admin
 * revoked. `partner.features` is the API's RESOLVED entitlement list (plan
 * defaults merged with per-partner overrides), so no plan name is needed here.
 */
export const useIsCrmFeatureGated = (feature: FEATURES) => {
  const { type, partner } = useAuthContext();
  return type.isBoomCRM && !partner?.features.includes(feature);
};

/**
 * Listing settings (listing templates) ride the showings entitlement — CRM-908
 * puts "the Showings calendar and Listing settings" under the one flag. Kept as a
 * named hook so the rule lives in one place (`isListingSettings` in the Auth
 * provider) should product move it.
 */
export const useIsListingSettingsGated = () => {
  const { type } = useAuthContext();
  return type.isBoomCRM && !type.isListingSettings;
};

/**
 * Workflows resolve through two keys — the plan-managed `workflows` entitlement
 * and its legacy alias `crm_feature_workflows` (see `isCrmFeatureWorkflows` in
 * the Auth provider) — so the generic `useIsCrmFeatureGated` would misfire here.
 */
export const useIsWorkflowsGated = () => {
  const { type } = useAuthContext();
  return type.isBoomCRM && !type.isCrmFeatureWorkflows;
};

/**
 * Surfaces that exist only to mint leads (the contact-form builder and its tab)
 * are gated when the partner holds neither lead entitlement — mirrors the API's
 * `ensure_leads_access!` (CRM-759).
 */
export const useIsLeadsGated = () => {
  const { type } = useAuthContext();
  return type.isBoomCRM && !type.isTenantLeads && !type.isOwnerLeads;
};
